import type { ReactNode } from "react";
import { createContext, useState, useEffect, useContext } from "react";
import {
  signInAnonymously,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from "firebase/auth";
import type { User } from "firebase/auth";
import { auth } from "src/initialiseFirebase";

type AuthContextType = {
  user: User | null;
  loading: boolean;
  signInAnonymous: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  isAnonymous: boolean;
};

const AuthContext: React.Context<AuthContextType> = createContext<AuthContextType>({
  user: null,
  loading: true,
  signInAnonymous: async () => {},
  signInWithGoogle: async () => {},
  signOut: async () => {},
  isAnonymous: true
});

type Props = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: Props): ReactNode => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInAnonymous = async (): Promise<void> => {
    try {
      await signInAnonymously(auth);
    } catch (error) {
      console.error("Error signing in anonymously:", error);
      throw error;
    }
  };

  const signInWithGoogle = async (): Promise<void> => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google:", error);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  const isAnonymous = user ? user.isAnonymous : true;

  const value: AuthContextType = {
    user,
    loading,
    signInAnonymous,
    signInWithGoogle,
    signOut,
    isAnonymous
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext<AuthContextType>(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
